import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { copyToClipboard } from '../../../helpers/copyToClipboard';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DialogSuppressionService } from '../../../services/dialog-suppression.service';
import { AuthService } from '../../../services/auth.service';
import {
  IntegrationService,
  MoodleConnectionStatus,
} from '../../../services/integration.service';
import { LmsRegistryService } from '../../../services/lms-registry.service';
import {
  AutoCourseSyncMode,
  LmsRegistration,
  MoodleConnectionProbeResult,
  ProgressSource,
  ProgressSourceInfo,
  SyncedCoursePurgePlan,
} from '../../../services/lms-registry.service';
import { AgentRefreshService } from '../../../agent/agent-refresh.service';
import { finalize } from 'rxjs';
import { formatMegabytes } from '../../../helpers/formatNumbers';
import { MoodleService, MoodleSyncSettings } from '../../../services/moodle.service';
import { UsageService } from '../../../services/usage.service';

type MoodleSetupCheckStatus = 'success' | 'warning' | 'info' | 'danger';

interface MoodleSetupCheckResult {
  status: MoodleSetupCheckStatus;
  icon: string;
  titleKey: string;
  messageKey: string;
}

@Component({
  selector: 'app-moodle-connect-dialog',
  templateUrl: './moodle-connect-dialog.component.html',
  styleUrl: './moodle-connect-dialog.component.scss',
})
export class MoodleConnectDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  loading = true;
  connecting = false;
  status: MoodleConnectionStatus = { connected: false };
  syncSettings: MoodleSyncSettings = { sync_frequency: 'NEVER' };
  moodleSites: LmsRegistration[] = [];
  moodleSitesLoading = false;
  moodleSitesRefreshing = false;
  moodleSitesPollActive = false;
  moodleSitesPollCount = 0;
  siteBusy: Record<string, boolean> = {};
  /** Eigener Schalter für „Synchronisierte Kurse leeren": der Spinner gehört
   *  an diesen Knopf, nicht an den Pause-Knopf, der `siteBusy` anzeigt. */
  purgeBusy: Record<string, boolean> = {};
  /** „Moodle prüfen" (Fortschrittsquellen) — Spinner nur an diesem Knopf. */
  progressCheckBusy: Record<string, boolean> = {};
  primaryTransitionSiteId: string | null = null;
  checkResult: MoodleSetupCheckResult | null = null;

  /** Signed-plugin-channel pause state (different from the
   *  `sync_frequency` content-sync below). `paused=true` means every
   *  moodle_plugin/legacy_moodle LmsToolRegistration the client owns
   *  is paused; `mixed=true` signals a partial pause caused by
   *  duplicate-registration data integrity (rare but visible). */
  pauseState: {
    total: number;
    paused_count: number;
    paused: boolean;
    mixed: boolean;
  } = { total: 0, paused_count: 0, paused: false, mixed: false };
  /** True while a pause/resume call is in flight. Locks both buttons. */
  pauseBusy = false;
  private moodleSitesPollHandle: ReturnType<typeof setTimeout> | null = null;
  private primaryTransitionHandle: ReturnType<typeof setTimeout> | null = null;

  constructor(
    protected dialogRef: NbDialogRef<MoodleConnectDialogComponent>,
    protected integrationService: IntegrationService,
    protected moodleService: MoodleService,
    protected lmsRegistry: LmsRegistryService,
    protected authService: AuthService,
    protected usageService: UsageService,
    protected fb: FormBuilder,
    protected dialogService: NbDialogService,
    private dialogSuppression: DialogSuppressionService,
    protected nbToastrService: NbToastrService,
    private t: TranslateService,
    agentRefresh: AgentRefreshService
  ) {
    // lms.progress_source.set (Hausagent) announces `lms_registrations`.
    agentRefresh
      .on('lms_registrations')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.loadMoodleSites({ background: true }));
    this.form = this.fb.group({
      moodle_url: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]],
      api_key: [''],
    });
  }

  ngOnInit() {
    this.usageService.listApiKeys().subscribe({
      next: (apiKeys) => {
        const activeKey = apiKeys.find((key) => key.status === 'active') || apiKeys[0];
        if (activeKey && !this.form.value.api_key) {
          this.form.patchValue({ api_key: activeKey.clear_api_key });
        }
      },
    });
    this.integrationService.getMoodleStatus().subscribe({
      next: (status) => {
        this.status = status;
        this.loading = false;
        if (status.connected && status.api_url) {
          this.form.patchValue({ moodle_url: status.api_url });
          if (status.source !== 'moodle_plugin') {
            this.loadSyncSettings();
          }
        }
      },
      error: () => {
        this.loading = false;
      },
    });
    // Pause-state is fetched independently — the user may have a paused
    // signed-plugin channel even if the legacy WS connection (handled
    // by IntegrationService.getMoodleStatus above) reports disconnected.
    this.loadPauseState();
    this.loadMoodleSites();
  }

  ngOnDestroy(): void {
    this.clearMoodleSitesPoll();
    this.moodleSitesPollActive = false;
    this.moodleSitesPollCount = 0;
    this.clearPrimaryTransition();
  }

  get hasLegacyManualConnection(): boolean {
    return this.status.connected && this.status.source !== 'moodle_plugin';
  }

  get hasMoodleSites(): boolean {
    return this.moodleSites.length > 0;
  }

  get activeMoodleSites(): LmsRegistration[] {
    return this.moodleSites.filter((site) => !this.isSiteDisconnected(site));
  }

  get hasActiveMoodleSite(): boolean {
    return this.activeMoodleSites.length > 0;
  }

  get primaryMoodleSite(): LmsRegistration | null {
    return (
      this.activeMoodleSites.find((site) => site.is_primary) ||
      this.activeMoodleSites[0] ||
      null
    );
  }

  private get syncSettingsRegistrationId(): string | null {
    return this.primaryMoodleSite?.id || null;
  }

  get showConnectionForm(): boolean {
    return !this.hasLegacyManualConnection && !this.hasActiveMoodleSite;
  }

  get showMoodleSitesSection(): boolean {
    // Avoid a misleading mixed state: a legacy/manual WS-token
    // connection can be active even when no signed Moodle-plugin
    // registration exists yet. In that case only render the legacy
    // connection block below.
    return this.moodleSitesLoading || this.hasMoodleSites || !this.hasLegacyManualConnection;
  }

  get moodleSitesPollDisplayCount(): number {
    return Math.max(this.moodleSitesPollCount, 1);
  }

  displayMoodleDomain(site: LmsRegistration): string {
    const raw = site.moodle_site_url || site.display_name || '';
    try {
      return new URL(raw).hostname || raw;
    } catch {
      return raw.replace(/^https?:\/\//, '').replace(/\/+$/, '') || site.display_name || '';
    }
  }

  /** Hosts whose /favicon.ico already failed to load this session —
   *  shared across dialog instances so a dead favicon is only tried
   *  once (same pattern as the tutor-card logo registry). */
  private static readonly failedFaviconOrigins = new Set<string>();

  siteFaviconUrl(site: LmsRegistration): string | null {
    const raw = (site.moodle_site_url || '').trim();
    if (!raw) return null;
    try {
      const origin = new URL(raw).origin;
      if (MoodleConnectDialogComponent.failedFaviconOrigins.has(origin)) {
        return null;
      }
      return `${origin}/favicon.ico`;
    } catch {
      return null;
    }
  }

  onSiteFaviconError(site: LmsRegistration) {
    try {
      MoodleConnectDialogComponent.failedFaviconOrigins.add(
        new URL(site.moodle_site_url || '').origin
      );
    } catch {
      // no valid origin — nothing to remember
    }
  }

  loadMoodleSites(options: { background?: boolean; fromPoll?: boolean } = {}) {
    const background = !!options.background;
    if (background) {
      this.moodleSitesRefreshing = true;
    } else {
      this.moodleSitesLoading = true;
    }
    this.lmsRegistry.listMoodleSites().subscribe({
      next: (sites) => {
        if (options.fromPoll) {
          this.moodleSitesPollCount += 1;
        }
        this.moodleSites = this.sortPrimarySiteFirst(sites);
        this.moodleSitesLoading = false;
        this.moodleSitesRefreshing = false;
        this.scheduleMoodleSitesPollIfPending();
        if (this.hasActiveMoodleSite) {
          this.loadSyncSettings();
        }
      },
      error: () => {
        if (!background) {
          this.moodleSites = [];
        }
        this.moodleSitesLoading = false;
        this.moodleSitesRefreshing = false;
        this.clearMoodleSitesPoll();
        this.moodleSitesPollActive = false;
        this.moodleSitesPollCount = 0;
      },
    });
  }

  private hasMoodleSiteWaitingForToken(): boolean {
    return this.moodleSites.some((site) =>
      site.source === 'moodle_plugin' &&
      !this.isSiteDisconnected(site) &&
      !site.has_ws_token &&
      !site.ws_token_revoked_at
    );
  }

  private scheduleMoodleSitesPollIfPending(): void {
    this.clearMoodleSitesPoll();
    if (!this.hasMoodleSiteWaitingForToken()) {
      this.moodleSitesPollActive = false;
      this.moodleSitesPollCount = 0;
      return;
    }
    this.moodleSitesPollActive = true;
    this.moodleSitesPollHandle = setTimeout(() => {
      this.moodleSitesPollHandle = null;
      if (!this.moodleSitesLoading && !this.moodleSitesRefreshing) {
        this.loadMoodleSites({ background: true, fromPoll: true });
      }
    }, 3000);
  }

  private clearMoodleSitesPoll(): void {
    if (!this.moodleSitesPollHandle) return;
    clearTimeout(this.moodleSitesPollHandle);
    this.moodleSitesPollHandle = null;
  }

  trackMoodleSiteById(_index: number, site: LmsRegistration): string {
    return site.id;
  }

  private sortPrimarySiteFirst(sites: LmsRegistration[]): LmsRegistration[] {
    return [...sites].sort((a, b) => Number(!!b.is_primary) - Number(!!a.is_primary));
  }

  private promotePrimarySite(sites: LmsRegistration[], primaryId: string): LmsRegistration[] {
    const selected = sites.find((site) => site.id === primaryId);
    const rest = sites.filter((site) => site.id !== primaryId);
    if (!selected) {
      return sites.map((site) => ({ ...site, is_primary: false }));
    }
    return [
      { ...selected, is_primary: true },
      ...rest.map((site) => ({ ...site, is_primary: false })),
    ];
  }

  private markPrimaryTransition(siteId: string): void {
    if (this.primaryTransitionHandle) {
      clearTimeout(this.primaryTransitionHandle);
    }
    this.primaryTransitionSiteId = siteId;
    this.primaryTransitionHandle = setTimeout(() => {
      if (this.primaryTransitionSiteId === siteId) {
        this.primaryTransitionSiteId = null;
      }
      this.primaryTransitionHandle = null;
    }, 1200);
  }

  private clearPrimaryTransition(): void {
    if (this.primaryTransitionHandle) {
      clearTimeout(this.primaryTransitionHandle);
      this.primaryTransitionHandle = null;
    }
    this.primaryTransitionSiteId = null;
  }

  get hasApiKeys(): boolean {
    return this.usageService.apiKeys$.value.length > 0;
  }

  siteStatusKey(site: LmsRegistration): string {
    return `dialogs.moodleConnect.siteStatus.${this.siteEffectiveStatus(site)}`;
  }

  siteEffectiveStatus(site: LmsRegistration): string {
    const healthStatus = (site.health_status || '').toLowerCase();
    if (healthStatus && !['ok', 'unknown'].includes(healthStatus)) {
      return healthStatus;
    }
    return site.connection_status || 'unknown';
  }

  siteInfoMessageKey(site: LmsRegistration): string | null {
    const status = this.siteEffectiveStatus(site);
    if (this.isSiteDisconnected(site)) {
      return 'dialogs.moodleConnect.siteInfo.disconnected';
    }
    if (
      status === 'token_missing' ||
      status === 'site_token_unavailable' ||
      (site.has_ws_token === false && !site.ws_token_revoked_at)
    ) {
      return 'dialogs.moodleConnect.siteInfo.tokenMissing';
    }
    if (status === 'token_revoked' || site.ws_token_revoked_at) {
      return 'dialogs.moodleConnect.siteInfo.tokenRevoked';
    }
    if (status === 'token_invalid') {
      return 'dialogs.moodleConnect.siteInfo.tokenInvalid';
    }
    if (status === 'paused_api_key_missing') {
      return 'dialogs.moodleConnect.siteInfo.apiKeyMissing';
    }
    if (status === 'paused_api_key_rejected') {
      return 'dialogs.moodleConnect.siteInfo.apiKeyRejected';
    }
    return null;
  }

  siteInfoStatus(site: LmsRegistration): 'warning' | 'danger' | 'info' {
    const status = this.siteEffectiveStatus(site);
    if (['token_invalid', 'paused_api_key_rejected'].includes(status)) {
      return 'danger';
    }
    if (this.isSiteDisconnected(site)) {
      return 'info';
    }
    return 'warning';
  }

  private actionErrorCode(err: any): string | null {
    const detail = err?.error?.detail;
    return (
      detail?.code ||
      detail?.plugin_response?.error ||
      detail?.plugin_response?.code ||
      detail?.plugin_response?.errorcode ||
      null
    );
  }

  private applySiteStatus(siteId: string, status: string): void {
    this.moodleSites = this.moodleSites.map((site) =>
      site.id === siteId
        ? {
            ...site,
            connection_status:
              status === 'paused_api_key_missing' ||
              status === 'paused_api_key_rejected'
                ? 'paused'
                : site.connection_status,
            health_status: status,
          }
        : site
    );
  }

  private applyExpectedTokenError(site: LmsRegistration, err: any): boolean {
    const code = this.actionErrorCode(err);
    const status = this.expectedTokenStatusFromCode(code);
    if (!status) return false;
    this.applySiteStatus(site.id, status);
    return true;
  }

  private expectedTokenStatusFromCode(code: string | null): string | null {
    const statusByCode: Record<string, string> = {
      moodle_plugin_api_key_missing: 'paused_api_key_missing',
      api_key_missing: 'paused_api_key_missing',
      moodle_plugin_api_key_rejected: 'paused_api_key_rejected',
      api_key_rejected: 'paused_api_key_rejected',
      token_missing: 'token_missing',
      token_revoked: 'token_revoked',
      token_invalid: 'token_invalid',
      site_token_unavailable: 'site_token_unavailable',
    };
    return code ? statusByCode[code] || null : null;
  }

  siteNbStatus(site: LmsRegistration): 'success' | 'warning' | 'danger' {
    const status = this.siteEffectiveStatus(site);
    if (['connected', 'token_ready', 'ok'].includes(status)) return 'success';
    if ([
      'registering',
      'paused',
      'paused_api_key_missing',
      'paused_api_key_rejected',
      'token_missing',
      'token_revoked',
      'superseded',
    ].includes(status)) {
      return 'warning';
    }
    return 'danger';
  }

  isSitePaused(site: LmsRegistration): boolean {
    return site.connection_status === 'paused' || !!site.disabled_at;
  }

  isSiteDisconnected(site: LmsRegistration): boolean {
    return ['disconnected', 'superseded', 'token_revoked'].includes(
      this.siteEffectiveStatus(site),
    );
  }

  /** Forum import is super_admin-only while it is pre-production: it pulls
   *  learner-authored posts into the knowledge base, so customers should not
   *  be able to switch it on before the data-protection side (DPA, learner
   *  information duties) is settled. The backend enforces the same rule —
   *  this getter only hides the control. */
  get isSuperAdmin(): boolean {
    const payload = this.authService.accessTokenPayload$.value;
    return (payload?.role || '').toLowerCase() === 'super_admin';
  }

  get canManagePrimarySite(): boolean {
    const payload = this.authService.accessTokenPayload$.value;
    const role = (payload?.role || '').toLowerCase();
    if (['super_admin', 'admin', 'manager'].includes(role)) return true;
    const perms = (payload?.feature_set_permissions || []).map((p) => String(p).toUpperCase());
    return role === 'user' && !perms.includes('DOZENT');
  }

  private siteActionErrorMessage(err: any, fallbackKey: string): string {
    const code = this.actionErrorCode(err);
    if (!code) return this.t.instant(fallbackKey);
    const translated = this.t.instant(`dialogs.moodleConnect.siteActionErrors.${code}`);
    return translated === `dialogs.moodleConnect.siteActionErrors.${code}`
      ? this.t.instant('dialogs.moodleConnect.siteActionErrors.unknown')
      : translated;
  }

  onPauseSite(site: LmsRegistration) {
    if (this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    this.siteBusy[site.id] = true;
    this.lmsRegistry.pauseRegistration(site.id).subscribe({
      next: () => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        this.loadPauseState();
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.sitePausedToast'),
          this.t.instant('toasts.success')
        );
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.pauseError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  onResumeSite(site: LmsRegistration) {
    if (this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    this.siteBusy[site.id] = true;
    this.lmsRegistry.resumeRegistration(site.id).subscribe({
      next: () => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        this.loadPauseState();
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.siteResumedToast'),
          this.t.instant('toasts.success')
        );
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        if (this.applyExpectedTokenError(site, err)) return;
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.resumeError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  onDisconnectSite(site: LmsRegistration) {
    if (this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.moodleConnect.disconnectConfirmTitle',
          message: 'dialogs.moodleConnect.disconnectConfirmMessage',
          confirmLabel: 'dialogs.moodleConnect.disconnectShort',
          confirmStatus: 'primary',
          cancelStatus: 'basic',
        },
        hasBackdrop: true,
      })
      .onClose
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.siteBusy[site.id] = true;
        this.lmsRegistry
          .disconnectRegistration(site.id, 'Disconnected from portal')
          .subscribe({
            next: () => {
              this.siteBusy[site.id] = false;
              this.loadMoodleSites();
              this.loadPauseState();
              this.nbToastrService.info(
                this.t.instant('dialogs.moodleConnect.siteDisconnectedToast'),
                ''
              );
            },
            error: (err) => {
              this.siteBusy[site.id] = false;
              this.nbToastrService.danger(
                this.siteActionErrorMessage(err, 'dialogs.moodleConnect.disconnectError'),
                this.t.instant('toasts.error')
              );
            },
          });
      });
  }

  onSetPrimarySite(site: LmsRegistration) {
    if (!this.canManagePrimarySite || site.is_primary || this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    const previousSites = this.moodleSites.map((s) => ({ ...s }));
    this.siteBusy[site.id] = true;
    this.markPrimaryTransition(site.id);
    this.moodleSites = this.promotePrimarySite(this.moodleSites, site.id);
    this.lmsRegistry.setPrimaryRegistration(site.id).subscribe({
      next: (updated) => {
        this.siteBusy[site.id] = false;
        const primaryId = updated?.id || site.id;
        this.markPrimaryTransition(primaryId);
        this.moodleSites = this.promotePrimarySite(this.moodleSites, primaryId);
        this.loadSyncSettings();
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.primarySavedToast'),
          this.t.instant('toasts.success')
        );
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        this.clearPrimaryTransition();
        this.moodleSites = previousSites;
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.primaryError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  onPrimaryToggle(site: LmsRegistration, enabled: boolean) {
    if (!enabled) {
      if (site.is_primary) {
        this.moodleSites = this.moodleSites.map((s) =>
          s.id === site.id ? { ...s, is_primary: true } : s
        );
      }
      return;
    }
    if (site.is_primary) return;
    this.onSetPrimarySite(site);
  }

  onPluginChannelToggle(site: LmsRegistration, enabled: boolean) {
    if (enabled) {
      this.onResumeSite(site);
    } else {
      this.onPauseSite(site);
    }
  }

  onPluginChannelAction(site: LmsRegistration) {
    if (this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    if (this.isSitePaused(site)) {
      this.onResumeSite(site);
    } else {
      this.onPauseSite(site);
    }
  }

  /** Put a toggle flag back after a cancelled confirmation.
   *
   *  Looks the row up again instead of writing to the captured `site`: the
   *  background poll can swap `moodleSites` for fresh objects while the
   *  confirmation is open, and the visible toggle is bound to the CURRENT
   *  row. Writing the flag is what actually moves the toggle — see
   *  toggle-confirm-revert.spec.ts for why the model has to change. */
  private revertSiteToggle<K extends keyof LmsRegistration>(
    siteId: string,
    field: K,
    value: LmsRegistration[K],
  ): void {
    const current = this.moodleSites.find((s) => s.id === siteId);
    if (current) current[field] = value;
  }

  onMoodleUserPiiToggle(site: LmsRegistration, enabled: boolean) {
    if (this.siteBusy[site.id]) return;
    // nb-toggle has already flipped itself visually, but Angular's
    // `[checked]` binding still holds the OLD value — so nothing writes the
    // toggle back when the confirmation is cancelled (it just stayed on).
    // Mirror the flip into the bound model first; restoring `previous`
    // below is then a real change and the toggle snaps back. Same pattern
    // as onStructureParseSiteToggle.
    const previous = !!site.moodle_user_pii_sync_enabled;
    site.moodle_user_pii_sync_enabled = enabled;
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: enabled
          ? 'dialogs.moodleConnect.userPiiEnableDialog.title'
          : 'dialogs.moodleConnect.userPiiDisableDialog.title',
        message: enabled
          ? 'dialogs.moodleConnect.userPiiEnableDialog.message'
          : 'dialogs.moodleConnect.userPiiDisableDialog.message',
        confirmLabel: enabled
          ? 'dialogs.moodleConnect.userPiiEnableDialog.confirm'
          : 'dialogs.moodleConnect.userPiiDisableDialog.confirm',
        cancelLabel: 'buttons.cancel',
        confirmStatus: 'primary',
        cancelStatus: 'basic',
      },
      hasBackdrop: true,
    });
    ref.onClose.subscribe((confirmed) => {
      if (!confirmed) {
        this.revertSiteToggle(site.id, 'moodle_user_pii_sync_enabled', previous);
        return;
      }
      if (enabled) {
        this.onEnableMoodleUserPii(site);
      } else {
        this.onDisableMoodleUserPii(site);
      }
    });
  }

  onEnableMoodleUserPii(site: LmsRegistration) {
    if (this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    this.siteBusy[site.id] = true;
    this.lmsRegistry.enableMoodleUserPiiSync(site.id).subscribe({
      next: (result) => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        const failed = result.backfill_counts?.['courses_failed'] || 0;
        if (failed > 0) {
          this.nbToastrService.warning(
            this.t.instant('dialogs.moodleConnect.userPiiSyncPartialToast'),
            this.t.instant('dialogs.moodleConnect.userPiiSyncPartialTitle')
          );
        } else {
          this.nbToastrService.success(
            this.t.instant('dialogs.moodleConnect.userPiiSyncQueuedToast'),
            this.t.instant('dialogs.moodleConnect.userPiiSyncQueuedTitle')
          );
        }
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        // Re-read the server state so a failed request doesn't leave the
        // toggle showing something that never happened.
        this.loadMoodleSites();
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.userPiiSyncError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  onDisableMoodleUserPii(site: LmsRegistration) {
    if (this.siteBusy[site.id]) return;
    this.siteBusy[site.id] = true;
    this.lmsRegistry.disableMoodleUserPiiSync(site.id).subscribe({
      next: () => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.userPiiClearedToast'),
          this.t.instant('dialogs.moodleConnect.userPiiClearedTitle')
        );
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        // Re-read the server state so a failed request doesn't leave the
        // toggle showing something that never happened.
        this.loadMoodleSites();
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.userPiiClearError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  onForumSyncToggle(site: LmsRegistration, enabled: boolean) {
    if (this.siteBusy[site.id]) return;
    // nb-toggle has already flipped itself visually, but Angular's
    // `[checked]` binding still holds the OLD value — so nothing writes the
    // toggle back when the confirmation is cancelled (it just stayed on).
    // Mirror the flip into the bound model first; restoring `previous`
    // below is then a real change and the toggle snaps back. Same pattern
    // as onStructureParseSiteToggle.
    const previous = !!site.moodle_forum_sync_enabled;
    site.moodle_forum_sync_enabled = enabled;
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: enabled
          ? 'dialogs.moodleConnect.forumSyncEnableDialog.title'
          : 'dialogs.moodleConnect.forumSyncDisableDialog.title',
        message: enabled
          ? 'dialogs.moodleConnect.forumSyncEnableDialog.message'
          : 'dialogs.moodleConnect.forumSyncDisableDialog.message',
        confirmLabel: enabled
          ? 'dialogs.moodleConnect.forumSyncEnableDialog.confirm'
          : 'dialogs.moodleConnect.forumSyncDisableDialog.confirm',
        cancelLabel: 'buttons.cancel',
        // Enabling is the dangerous direction here, not disabling: it starts
        // importing learner posts. Disabling only deletes them again.
        confirmStatus: 'primary',
        cancelStatus: 'basic',
      },
      hasBackdrop: true,
    });
    ref.onClose.subscribe((confirmed) => {
      if (!confirmed) {
        this.revertSiteToggle(site.id, 'moodle_forum_sync_enabled', previous);
        return;
      }
      if (enabled) {
        this.onEnableForumSync(site);
      } else {
        this.onDisableForumSync(site);
      }
    });
  }

  /** Agents may change content in this Moodle — the customer's consent.
   *  Switching ON asks first (rule 17: mirror the flip into the model so
   *  "Abbrechen" can snap the toggle back); switching OFF just applies. */
  onAgentWritesToggle(site: LmsRegistration, enabled: boolean) {
    if (this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    const previous = !!site.mcp_admin_enabled;
    site.mcp_admin_enabled = enabled;
    if (!enabled) {
      this.applyAgentWrites(site, false);
      return;
    }
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'dialogs.moodleConnect.agentWritesEnableDialog.title',
        message: 'dialogs.moodleConnect.agentWritesEnableDialog.message',
        confirmLabel: 'dialogs.moodleConnect.agentWritesEnableDialog.confirm',
        cancelLabel: 'buttons.cancel',
        confirmStatus: 'primary',
        cancelStatus: 'basic',
      },
      hasBackdrop: true,
    });
    ref.onClose.subscribe((confirmed) => {
      if (confirmed) this.applyAgentWrites(site, true);
      else this.revertSiteToggle(site.id, 'mcp_admin_enabled', previous);
    });
  }

  private applyAgentWrites(site: LmsRegistration, enabled: boolean) {
    this.siteBusy[site.id] = true;
    const call$ = enabled
      ? this.lmsRegistry.enableAgentWrites(site.id)
      : this.lmsRegistry.disableAgentWrites(site.id);
    call$.pipe(finalize(() => (this.siteBusy[site.id] = false))).subscribe({
      next: () => {
        this.loadMoodleSites();
        this.nbToastrService.success(
          this.t.instant(
            enabled
              ? 'dialogs.moodleConnect.agentWritesEnabledToast'
              : 'dialogs.moodleConnect.agentWritesDisabledToast'
          ),
          ''
        );
      },
      error: (err) => {
        // Re-read the server state so the toggle never shows what did not happen.
        this.loadMoodleSites();
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.agentWritesError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  /** The plugin computes Completion Progress bars (block_alphabees 3.2+). */
  canUseCompletionProgress(site: LmsRegistration): boolean {
    return (site.moodle_plugin_capabilities || []).includes('progress_trackers.completion_progress');
  }

  progressSourceInfo(site: LmsRegistration, key: string): ProgressSourceInfo | null {
    return site.progress_sources_discovered?.sources?.find((s) => s.key === key) || null;
  }

  /** „Moodle prüfen": which progress sources this Moodle has (counts only). */
  onCheckProgressSources(site: LmsRegistration) {
    if (this.progressCheckBusy[site.id] || this.isSiteDisconnected(site)) return;
    this.progressCheckBusy[site.id] = true;
    this.lmsRegistry
      .discoverProgressSources(site.id)
      .pipe(finalize(() => (this.progressCheckBusy[site.id] = false)))
      .subscribe({
        next: () => this.loadMoodleSites({ background: true }),
        error: (err) => {
          this.nbToastrService.danger(
            this.siteActionErrorMessage(err, 'dialogs.moodleConnect.progressCheckError'),
            this.t.instant('toasts.error')
          );
        },
      });
  }

  /** One progress source for the whole Moodle, no fallback. Choosing the
   *  Completion Progress bars asks first; the pick is mirrored into the
   *  model so "Abbrechen" puts the select back (same as rule 17). */
  onProgressSourceChange(site: LmsRegistration, source: ProgressSource) {
    const previous: ProgressSource = site.progress_source || 'course_completion';
    if (source === previous || this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    site.progress_source = source;
    if (source === 'course_completion') {
      this.applyProgressSource(site, source, previous);
      return;
    }
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'dialogs.moodleConnect.progressSourceDialog.title',
        message: 'dialogs.moodleConnect.progressSourceDialog.message',
        confirmLabel: 'dialogs.moodleConnect.progressSourceDialog.confirm',
        cancelLabel: 'buttons.cancel',
        confirmStatus: 'primary',
        cancelStatus: 'basic',
      },
      hasBackdrop: true,
    });
    ref.onClose.subscribe((confirmed) => {
      if (confirmed) this.applyProgressSource(site, source, previous);
      else this.revertSiteToggle(site.id, 'progress_source', previous);
    });
  }

  private applyProgressSource(site: LmsRegistration, source: ProgressSource, previous: ProgressSource) {
    this.siteBusy[site.id] = true;
    this.lmsRegistry
      .setProgressSource(site.id, source)
      .pipe(finalize(() => (this.siteBusy[site.id] = false)))
      .subscribe({
        next: () => {
          this.loadMoodleSites({ background: true });
          this.nbToastrService.success(
            this.t.instant(
              source === 'completion_progress'
                ? 'dialogs.moodleConnect.progressSourceCompletionProgressToast'
                : 'dialogs.moodleConnect.progressSourceCourseCompletionToast'
            ),
            ''
          );
        },
        error: (err) => {
          this.revertSiteToggle(site.id, 'progress_source', previous);
          this.loadMoodleSites({ background: true });
          this.nbToastrService.danger(
            this.siteActionErrorMessage(err, 'dialogs.moodleConnect.progressSourceError'),
            this.t.instant('toasts.error')
          );
        },
      });
  }

  /** Instance-level Kurs-Didaktik (structure parse). No confirm dialog — it
   *  writes nothing into Moodle, just analyses synced course data. */
  onStructureParseSiteToggle(site: LmsRegistration, enabled: boolean) {
    if (this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    if (enabled) {
      // nb-toggle has already flipped itself visually. Mirror that into the
      // bound model, so cancelling can flip it BACK — leaving the model
      // untouched would show an "on" toggle for something never enabled.
      const previous = !!site.auto_structure_parse_enabled;
      site.auto_structure_parse_enabled = true;
      this.confirmDidacticsCost(
        'didactics-cost-site',
        () => this.applyStructureParseSite(site, true),
        () => this.revertSiteToggle(site.id, 'auto_structure_parse_enabled', previous),
      );
      return;
    }
    this.applyStructureParseSite(site, false);
  }

  /** Ask once before switching course didactics ON: it costs credits per
   *  teaching unit and runs for minutes. The operator may silence it, and
   *  that choice is remembered per user. Switching OFF is free — no dialog. */
  private confirmDidacticsCost(
    dialogKey: string,
    onConfirm: () => void,
    onCancel?: () => void,
  ): void {
    if (this.dialogSuppression.isSuppressed(dialogKey)) {
      onConfirm();
      return;
    }
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.didacticsCost.title',
          message: 'dialogs.didacticsCost.message',
          details: 'dialogs.didacticsCost.details',
          confirmLabel: 'dialogs.didacticsCost.confirm',
          cancelLabel: 'buttons.cancel',
          confirmStatus: 'primary',
          cancelStatus: 'basic',
          showSuppress: true,
        },
      })
      .onClose.subscribe((res: { confirmed?: boolean; suppress?: boolean } | undefined) => {
        if (!res?.confirmed) {
          onCancel?.();
          return;
        }
        if (res.suppress) this.dialogSuppression.suppress(dialogKey);
        onConfirm();
      });
  }

  private applyStructureParseSite(site: LmsRegistration, enabled: boolean) {
    this.siteBusy[site.id] = true;
    const obs = enabled
      ? this.lmsRegistry.enableStructureParseSite(site.id)
      : this.lmsRegistry.disableStructureParseSite(site.id);
    obs.subscribe({
      next: () => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        this.nbToastrService.success(
          this.t.instant(
            enabled
              ? 'dialogs.moodleConnect.structureParseEnabledToast'
              : 'dialogs.moodleConnect.structureParseDisabledToast',
          ),
          this.t.instant('dialogs.moodleConnect.structureParseTitle'),
        );
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.structureParseError'),
          this.t.instant('toasts.error'),
        );
      },
    });
  }

  onEnableForumSync(site: LmsRegistration) {
    if (this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    this.siteBusy[site.id] = true;
    this.lmsRegistry.enableForumSync(site.id).subscribe({
      next: () => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.forumSyncEnabledToast'),
          this.t.instant('dialogs.moodleConnect.forumSyncEnabledTitle')
        );
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        // Re-read the server state so a failed request doesn't leave the
        // toggle showing something that never happened.
        this.loadMoodleSites();
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.forumSyncError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  onDisableForumSync(site: LmsRegistration) {
    if (this.siteBusy[site.id]) return;
    this.siteBusy[site.id] = true;
    this.lmsRegistry.disableForumSync(site.id).subscribe({
      next: () => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.forumSyncDisabledToast'),
          this.t.instant('dialogs.moodleConnect.forumSyncDisabledTitle')
        );
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        // Re-read the server state so a failed request doesn't leave the
        // toggle showing something that never happened.
        this.loadMoodleSites();
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.forumSyncError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  onAutoCourseSyncToggle(site: LmsRegistration, enabled: boolean) {
    if (this.siteBusy[site.id]) return;
    // nb-toggle has already flipped itself visually, but Angular's
    // `[checked]` binding still holds the OLD value — so nothing writes the
    // toggle back when the confirmation is cancelled (it just stayed on).
    // Mirror the flip into the bound model first; restoring `previous`
    // below is then a real change and the toggle snaps back. Same pattern
    // as onStructureParseSiteToggle.
    const previous = !!site.auto_course_sync_enabled;
    site.auto_course_sync_enabled = enabled;
    if (enabled) {
      this.openAutoCourseSyncDialog(site, true, previous);
      return;
    }
    // Ausschalten allein gibt keinen Speicher frei (SIHK, 08.10.2026: 4,9 GB
    // blieben nach dem Abschalten stehen). Der Dialog fragt deshalb, was mit
    // den importierten Inhalten passiert — dafür braucht er die Zahlen.
    this.siteBusy[site.id] = true;
    this.lmsRegistry
      .previewSyncedCoursePurge(site.id)
      .pipe(finalize(() => (this.siteBusy[site.id] = false)))
      .subscribe({
        next: (plan) => this.openAutoCourseSyncDialog(site, false, previous, plan),
        // Ohne Vorschau bleibt es beim bisherigen Dialog: nur ausschalten.
        error: () => this.openAutoCourseSyncDialog(site, false, previous),
      });
  }

  private openAutoCourseSyncDialog(
    site: LmsRegistration,
    enabled: boolean,
    previous: boolean,
    purgePlan?: SyncedCoursePurgePlan,
  ) {
    const offerPurge = !enabled && !!purgePlan?.course_count;
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: enabled
          ? 'dialogs.moodleConnect.autoCourseSyncEnableDialog.title'
          : 'dialogs.moodleConnect.autoCourseSyncDisableDialog.title',
        message: enabled
          ? 'dialogs.moodleConnect.autoCourseSyncEnableDialog.message'
          : offerPurge
            ? 'dialogs.moodleConnect.autoCourseSyncDisableDialog.messageWithChoice'
            : 'dialogs.moodleConnect.autoCourseSyncDisableDialog.message',
        confirmLabel: enabled
          ? 'dialogs.moodleConnect.autoCourseSyncEnableDialog.confirm'
          : 'dialogs.moodleConnect.autoCourseSyncDisableDialog.confirm',
        cancelLabel: 'buttons.cancel',
        confirmStatus: 'primary',
        cancelStatus: 'basic',
        // Enable flow: pick which courses the auto-sync covers. Confirming
        // closes the dialog with the selected mode instead of `true`.
        options: enabled
          ? [
              {
                value: 'all',
                label: 'dialogs.moodleConnect.autoCourseSyncModeAll',
                description: 'dialogs.moodleConnect.autoCourseSyncModeAllDesc',
              },
              {
                value: 'placements',
                label: 'dialogs.moodleConnect.autoCourseSyncModePlacements',
                description: 'dialogs.moodleConnect.autoCourseSyncModePlacementsDesc',
              },
            ]
          : offerPurge
            ? [
                {
                  value: 'keep',
                  label: 'dialogs.moodleConnect.autoCourseSyncDisableKeep',
                  description: 'dialogs.moodleConnect.autoCourseSyncDisableKeepDesc',
                },
                {
                  value: 'purge',
                  label: 'dialogs.moodleConnect.autoCourseSyncDisablePurge',
                  // Fertig übersetzter Text: der translate-Pipe im Dialog gibt
                  // einen unbekannten Schlüssel unverändert zurück.
                  description: this.syncedCoursePurgeText(purgePlan!),
                },
              ]
            : undefined,
        // Behalten ist voreingestellt — Leeren muss man bewusst wählen.
        selectedOption: enabled
          ? site.auto_course_sync_mode || 'all'
          : offerPurge
            ? 'keep'
            : undefined,
      },
      hasBackdrop: true,
    });
    ref.onClose.subscribe((confirmed) => {
      if (!confirmed) {
        this.revertSiteToggle(site.id, 'auto_course_sync_enabled', previous);
        return;
      }
      if (enabled) {
        const mode: AutoCourseSyncMode =
          confirmed === 'placements' ? 'placements' : 'all';
        this.onEnableAutoCourseSync(site, mode);
      } else {
        this.onDisableAutoCourseSync(site, confirmed === 'purge');
      }
    });
  }

  /** Zusammenfassung für Dialoge: was geleert wird, was bleibt, was es
   *  für Agenten bedeutet. Größe in MB wie die Speicher-Kachel. */
  private syncedCoursePurgeText(plan: SyncedCoursePurgePlan): string {
    const parts = [
      this.t.instant('dialogs.moodleConnect.syncedCoursePurgeSummary', {
        courses: plan.course_count,
        files: plan.file_count,
        size: formatMegabytes(plan.total_bytes),
        kept: plan.kept_count,
      }),
    ];
    if (plan.agent_linked_count > 0) {
      parts.push(
        this.t.instant('dialogs.moodleConnect.syncedCoursePurgeAgentWarning', {
          count: plan.agent_linked_count,
        }),
      );
    }
    parts.push(this.t.instant('dialogs.moodleConnect.syncedCoursePurgeRestoreHint'));
    return parts.join(' ');
  }

  /** Eigener Einstieg für Instanzen, deren Auto-Sync schon aus ist — dort
   *  gibt es keinen Ausschalt-Dialog mehr, der fragen könnte. */
  onPurgeSyncedCoursesClick(site: LmsRegistration) {
    if (this.siteBusy[site.id] || this.purgeBusy[site.id]) return;
    this.purgeBusy[site.id] = true;
    this.lmsRegistry
      .previewSyncedCoursePurge(site.id)
      .pipe(finalize(() => (this.purgeBusy[site.id] = false)))
      .subscribe({
        next: (plan) => {
          if (!plan.course_count) {
            this.nbToastrService.info(
              this.t.instant('dialogs.moodleConnect.syncedCoursePurgeNothing'),
              this.t.instant('dialogs.moodleConnect.syncedCoursePurge'),
            );
            return;
          }
          this.dialogService
            .open(ConfirmDialogComponent, {
              context: {
                title: 'dialogs.moodleConnect.syncedCoursePurgeDialog.title',
                message: this.syncedCoursePurgeText(plan),
                confirmLabel: 'dialogs.moodleConnect.syncedCoursePurgeDialog.confirm',
                cancelLabel: 'buttons.cancel',
                confirmStatus: 'primary',
                cancelStatus: 'basic',
              },
              hasBackdrop: true,
            })
            .onClose.subscribe((confirmed) => {
              if (confirmed) this.runSyncedCoursePurge(site);
            });
        },
        error: (err) =>
          this.nbToastrService.danger(
            this.siteActionErrorMessage(err, 'dialogs.moodleConnect.syncedCoursePurgeError'),
            this.t.instant('toasts.error'),
          ),
      });
  }

  private runSyncedCoursePurge(site: LmsRegistration) {
    if (this.purgeBusy[site.id]) return;
    this.purgeBusy[site.id] = true;
    this.lmsRegistry
      .purgeSyncedCourses(site.id)
      .pipe(finalize(() => (this.purgeBusy[site.id] = false)))
      .subscribe({
        next: (result) => {
          this.nbToastrService.success(
            this.t.instant('dialogs.moodleConnect.syncedCoursePurgeStartedToast', {
              courses: result.course_count,
              size: formatMegabytes(result.total_bytes),
            }),
            this.t.instant('dialogs.moodleConnect.syncedCoursePurgeStartedTitle'),
          );
        },
        error: (err) =>
          this.nbToastrService.danger(
            err?.status === 409
              ? this.t.instant('dialogs.moodleConnect.syncedCoursePurgeAutoSyncOn')
              : this.siteActionErrorMessage(err, 'dialogs.moodleConnect.syncedCoursePurgeError'),
            this.t.instant('toasts.error'),
          ),
      });
  }

  onEnableAutoCourseSync(site: LmsRegistration, mode: AutoCourseSyncMode = 'all') {
    if (this.siteBusy[site.id] || this.isSiteDisconnected(site)) return;
    this.siteBusy[site.id] = true;
    this.lmsRegistry.enableAutoCourseSync(site.id, mode).subscribe({
      next: (result) => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        const skipped = result.skipped_quota || 0;
        if (skipped > 0) {
          this.nbToastrService.warning(
            this.t.instant('dialogs.moodleConnect.autoCourseSyncPartialToast', {
              queued: result.queued || 0,
              skipped,
            }),
            this.t.instant('dialogs.moodleConnect.autoCourseSyncPartialTitle')
          );
        } else {
          this.nbToastrService.success(
            this.t.instant('dialogs.moodleConnect.autoCourseSyncEnabledToast', {
              queued: result.queued || 0,
            }),
            this.t.instant('dialogs.moodleConnect.autoCourseSyncEnabledTitle')
          );
        }
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        // Re-read the server state so a failed request doesn't leave the
        // toggle showing something that never happened.
        this.loadMoodleSites();
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.autoCourseSyncError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  onDisableAutoCourseSync(site: LmsRegistration, purge = false) {
    if (this.siteBusy[site.id]) return;
    this.siteBusy[site.id] = true;
    this.lmsRegistry.disableAutoCourseSync(site.id).subscribe({
      next: () => {
        this.siteBusy[site.id] = false;
        this.loadMoodleSites();
        if (purge) {
          // Erst jetzt, mit Auto-Sync aus — der Server lehnt sonst ab (409).
          this.runSyncedCoursePurge(site);
          return;
        }
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.autoCourseSyncDisabledToast'),
          this.t.instant('dialogs.moodleConnect.autoCourseSyncDisabledTitle')
        );
      },
      error: (err) => {
        this.siteBusy[site.id] = false;
        // Re-read the server state so a failed request doesn't leave the
        // toggle showing something that never happened.
        this.loadMoodleSites();
        this.nbToastrService.danger(
          this.siteActionErrorMessage(err, 'dialogs.moodleConnect.autoCourseSyncError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  /** Fetch the aggregate pause-state across all of the client's
   *  moodle_plugin + legacy_moodle LmsToolRegistrations. Errors are
   *  swallowed silently — the pause feature is non-critical and we
   *  don't want to block the dialog from rendering if the al-tutor
   *  endpoint is down. */
  loadPauseState() {
    this.lmsRegistry.getMoodleIntegrationPauseState().subscribe({
      next: (s) => {
        this.pauseState = s;
      },
      error: () => {
        this.pauseState = { total: 0, paused_count: 0, paused: false, mixed: false };
      },
    });
  }

  /** Pause every moodle_plugin + legacy_moodle registration for the
   *  current client in one call. Affects ONLY the signed plugin
   *  channel + the al-tutor canonical-refresh worker — the
   *  KB-content sync driven by `sync_frequency` below is unrelated
   *  and keeps running, so re-embedding of course PDFs continues. */
  onPauseIntegration() {
    if (this.pauseBusy) return;
    this.pauseBusy = true;
    this.lmsRegistry.pauseMoodleIntegration().subscribe({
      next: () => {
        this.pauseBusy = false;
        this.loadPauseState();
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.pausedToast'),
          this.t.instant('toasts.success')
        );
      },
      error: () => {
        this.pauseBusy = false;
        this.nbToastrService.danger(
          this.t.instant('dialogs.moodleConnect.pauseError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  /** Inverse of `onPauseIntegration`. */
  onResumeIntegration() {
    if (this.pauseBusy) return;
    this.pauseBusy = true;
    this.lmsRegistry.resumeMoodleIntegration().subscribe({
      next: () => {
        this.pauseBusy = false;
        this.loadPauseState();
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.resumedToast'),
          this.t.instant('toasts.success')
        );
      },
      error: (err) => {
        this.pauseBusy = false;
        this.loadMoodleSites();
        this.loadPauseState();
        if (this.expectedTokenStatusFromCode(this.actionErrorCode(err))) return;
        this.nbToastrService.danger(
          this.t.instant('dialogs.moodleConnect.resumeError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  onConnect() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.connecting = true;
    const moodleUrl = this.normalizeUrl(this.form.value.moodle_url);

    this.lmsRegistry.probeMoodleConnection(moodleUrl).subscribe({
      next: (result) => {
        this.connecting = false;
        if (result.registration) {
          const withoutCurrent = this.moodleSites.filter(
            (site) => site.id !== result.registration?.id
          );
          this.moodleSites = [result.registration, ...withoutCurrent];
        }
        this.showPluginProbeStatus(result);
        this.loadMoodleSites();
        this.loadPauseState();
      },
      error: () => {
        this.connecting = false;
        this.setCheckResult(
          'danger',
          'close-circle-outline',
          'dialogs.moodleConnect.pluginSetupTitle',
          'dialogs.moodleConnect.checkError',
        );
      },
    });
  }

  clearCheckResult() {
    this.checkResult = null;
  }

  copySelectedApiKey() {
    const apiKey = this.form.value.api_key;
    if (!apiKey) return;
    copyToClipboard(apiKey);
    this.nbToastrService.success(
      this.t.instant('dialogs.moodleConnect.apiKeyCopied'),
      this.t.instant('toasts.success')
    );
  }

  private normalizeUrl(value: string): string {
    return (value || '').trim().replace(/\/+$/, '').toLowerCase();
  }

  private showPluginProbeStatus(result: MoodleConnectionProbeResult): void {
    const status = result.status || 'unknown';
    const config: Record<string, MoodleSetupCheckResult> = {
      ready: {
        status: 'success',
        icon: 'checkmark-circle-2-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupReady',
      },
      not_found: {
        status: 'info',
        icon: 'info-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupNotFound',
      },
      token_missing: {
        status: 'warning',
        icon: 'alert-circle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupTokenMissing',
      },
      token_revoked: {
        status: 'warning',
        icon: 'alert-triangle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupTokenRevoked',
      },
      token_invalid: {
        status: 'danger',
        icon: 'close-circle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupTokenInvalid',
      },
      paused: {
        status: 'warning',
        icon: 'pause-circle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupPaused',
      },
      disconnected: {
        status: 'warning',
        icon: 'alert-triangle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupDisconnected',
      },
      webservice_disabled: {
        status: 'danger',
        icon: 'close-circle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupWebserviceDisabled',
      },
      webservice_function_missing: {
        status: 'danger',
        icon: 'close-circle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupFunctionMissing',
      },
      unreachable: {
        status: 'danger',
        icon: 'wifi-off-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupUnreachable',
      },
      http_error: {
        status: 'danger',
        icon: 'close-circle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupHttpError',
      },
      moodle_ws_error: {
        status: 'danger',
        icon: 'close-circle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.pluginSetupWsError',
      },
      unknown: {
        status: 'danger',
        icon: 'close-circle-outline',
        titleKey: 'dialogs.moodleConnect.pluginSetupTitle',
        messageKey: 'dialogs.moodleConnect.checkError',
      },
    };
    this.checkResult = config[status] || config['unknown'];
  }

  private setCheckResult(
    status: MoodleSetupCheckStatus,
    icon: string,
    titleKey: string,
    messageKey: string,
  ): void {
    this.checkResult = { status, icon, titleKey, messageKey };
  }

  onDisconnect() {
    this.connecting = true;
    this.integrationService.disconnectMoodle().subscribe({
      next: () => {
        this.connecting = false;
        this.status = { connected: false };
        this.form.reset();
        this.nbToastrService.info(
          this.t.instant('dialogs.moodleConnect.disconnectSuccess'),
          ''
        );
      },
      error: () => {
        this.connecting = false;
      },
    });
  }

  loadSyncSettings() {
    this.moodleService.getSyncSettings(this.syncSettingsRegistrationId).subscribe({
      next: (settings) => {
        this.syncSettings = settings;
      },
      error: () => {
        // Silently fail — settings are optional
      },
    });
  }

  onSyncFrequencyChange(frequency: 'NEVER' | 'DAILY' | 'MONTHLY') {
    this.moodleService.updateSyncSettings(frequency, this.syncSettingsRegistrationId).subscribe({
      next: (settings) => {
        this.syncSettings = settings;
        this.nbToastrService.success(
          this.t.instant('dialogs.moodleConnect.syncSettingsSaved'),
          this.t.instant('toasts.success')
        );
      },
      error: () => {
        this.nbToastrService.danger(
          this.t.instant('dialogs.moodleConnect.syncSettingsError'),
          this.t.instant('toasts.error')
        );
      },
    });
  }

  close() {
    this.dialogRef.close(this.status.connected);
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }
}
